import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiShield, FiArrowRight, FiCheck, FiAlertCircle, FiInfo } from "@/lib/icons";
import { useLMICalculator } from "./_hooks/useLMICalculator";
import { lmiAvoidanceStrategies } from "./_data/lmiRates";
import { cn } from "@/lib/utils";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatValue,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  formatValue: (v: number) => string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium">{label}</label>
        <span className="text-lg font-bold text-primary">{formatValue(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
    </div>
  );
}

export function LMICalculatorPage() {
  const { inputs, results, updateInput, setDepositPercent } = useLMICalculator();

  const depositPresets = [5, 10, 15, 20];

  return (
    <>
      <SEO
        title="LMI Calculator"
        description="Calculate Lenders Mortgage Insurance (LMI) costs based on your deposit and property value."
        url="/calculators/lmi"
      />

      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-3 bg-accent/20 rounded-xl mb-6">
              <FiShield className="size-6 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              LMI Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Lenders Mortgage Insurance (LMI) is typically required when borrowing more than 80%
              of the property value. Calculate your estimated LMI costs below.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Calculator */}
            <div className="bg-background rounded-3xl border border-border p-8">
              <h2 className="font-serif text-xl mb-8">Enter Your Details</h2>

              <div className="space-y-8">
                <SliderInput
                  label="Property Value"
                  value={inputs.propertyValue}
                  onChange={(v) => updateInput("propertyValue", v)}
                  min={200000}
                  max={2000000}
                  step={10000}
                  formatValue={formatCurrency}
                />

                <div className="space-y-4">
                  <SliderInput
                    label="Your Deposit"
                    value={inputs.deposit}
                    onChange={(v) => updateInput("deposit", v)}
                    min={0}
                    max={inputs.propertyValue * 0.3}
                    step={5000}
                    formatValue={formatCurrency}
                  />

                  <div className="flex flex-wrap gap-2">
                    {depositPresets.map((percent) => (
                      <button
                        key={percent}
                        onClick={() => setDepositPercent(percent)}
                        className={cn(
                          "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                          Math.abs(inputs.deposit - inputs.propertyValue * (percent / 100)) < 1000
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        )}
                      >
                        {percent}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-4 pt-6 border-t border-border">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inputs.hasFamilyGuarantee}
                      onChange={(e) => updateInput("hasFamilyGuarantee", e.target.checked)}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                    />
                    <div>
                      <span className="font-medium">Family Guarantee</span>
                      <p className="text-xs text-muted-foreground">
                        A family member provides additional security
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inputs.isFirstHomeBuyer}
                      onChange={(e) => updateInput("isFirstHomeBuyer", e.target.checked)}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                    />
                    <div>
                      <span className="font-medium">First Home Buyer</span>
                      <p className="text-xs text-muted-foreground">
                        May qualify for First Home Guarantee scheme
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6">
              {/* LVR Indicator */}
              <div className="bg-background rounded-2xl border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-medium">Loan to Value Ratio (LVR)</span>
                  <span
                    className={cn(
                      "text-2xl font-bold",
                      results.lvr <= 80 ? "text-accent" : results.lvr <= 90 ? "text-primary" : "text-destructive"
                    )}
                  >
                    {results.lvr.toFixed(1)}%
                  </span>
                </div>
                <div className="h-4 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all duration-500",
                      results.lvr <= 80 ? "bg-accent" : results.lvr <= 90 ? "bg-primary" : "bg-destructive"
                    )}
                    style={{ width: `${Math.min(results.lvr, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>0%</span>
                  <span className="text-accent font-medium">80% (No LMI)</span>
                  <span>100%</span>
                </div>
              </div>

              {/* LMI Result */}
              <div
                className={cn(
                  "rounded-2xl p-6",
                  results.lmiRequired ? "bg-muted border-2 border-border" : "bg-accent/10 border-2 border-accent/30"
                )}
              >
                {results.lmiRequired ? (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <FiAlertCircle className="size-5 text-primary" />
                      <span className="font-medium text-primary">LMI Required</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Loan Amount</span>
                        <span className="font-medium">{formatCurrency(results.loanAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">LMI Rate</span>
                        <span className="font-medium">{results.lmiRate}% of loan</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-border">
                        <span className="font-medium">Estimated LMI Premium</span>
                        <span className="text-2xl font-bold text-primary">
                          {formatCurrency(results.lmiPremium)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Includes approximately 10% stamp duty on LMI premium
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <FiCheck className="size-12 text-accent mx-auto mb-3" />
                    <p className="font-medium text-primary text-lg">No LMI Required!</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {inputs.hasFamilyGuarantee
                        ? "Family guarantee eliminates LMI requirement"
                        : "Your deposit is 20% or more"}
                    </p>
                  </div>
                )}
              </div>

              {/* How to avoid */}
              {results.lmiRequired && (
                <div className="bg-background rounded-2xl border border-border p-6">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <FiInfo className="size-5 text-primary" />
                    To avoid LMI you need
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Required deposit (20%)</span>
                      <span className="font-medium">{formatCurrency(results.depositNeededToAvoid)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Your deposit</span>
                      <span className="font-medium">{formatCurrency(inputs.deposit)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-border">
                      <span className="font-medium">Additional needed</span>
                      <span className="text-xl font-bold text-primary">
                        {formatCurrency(results.additionalDepositRequired)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="bg-primary text-primary-foreground rounded-2xl p-6 text-center">
                <p className="text-primary-foreground/90 mb-2">
                  {results.lmiRequired ? "Want to explore LMI-free options?" : "Ready to get started?"}
                </p>
                <p className="font-serif text-xl mb-4">We can help find the best solution</p>
                <Button asChild className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to="/contact">
                    Get in Touch
                    <FiArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Ways to Avoid LMI */}
          <div className="mt-16 max-w-4xl mx-auto">
            <h2 className="font-serif text-2xl text-center mb-8">Ways to Avoid LMI</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lmiAvoidanceStrategies.map((strategy) => (
                <div key={strategy.title} className="bg-background rounded-xl p-5 border border-border">
                  <h3 className="font-medium mb-2">{strategy.title}</h3>
                  <p className="text-sm text-muted-foreground">{strategy.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> LMI premiums are indicative only and vary by lender and insurer.
              Actual premiums may differ based on loan type, employment status, and other factors.
              LMI can usually be added to your loan amount (capitalised). Contact us for accurate quotes.
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Disclaimer:</strong> This page provides general information only and has been
              prepared without considering your objectives, financial situation or needs. We recommend
              that you consider whether it is appropriate for your circumstances and your full financial
              situation will need to be reviewed prior to acceptance of any offer or product. It does
              not constitute legal, tax or financial advice and you should always seek professional
              advice in relation to your individual circumstances.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
